"use client"

import clsx from "clsx"
import { NpColor } from "src/components/np-design/components/NpColor"
import { NpColorOption } from "src/components/np-design/components/NpColors"
import { Tooltip } from "src/components/styled/Tooltip"
import { useEventCallback } from "src/hooks/useEventCallback"
import { useArrowKeys } from "./useArrowKeys"

type ColorButtonProps = {
  option: NpColorOption
  index: number
  active: boolean
  onActiveIndexChange: (value: number) => void
}

const ColorButton = ({ option, index, active, onActiveIndexChange }: ColorButtonProps) => {
  const onClick = useEventCallback(() => onActiveIndexChange(index))

  return (
    <Tooltip text={option.name} placement="top">
      <div onClick={onClick} onKeyDown={undefined} className="group rounded-full outline-none">
        <div
          className={clsx("flex items-center justify-center rounded-full p-3 transition-all", {
            "group-hover:bg-np-green25": !active,
            "bg-np-green10": active,
          })}
        >
          <NpColor values={option.values} />
        </div>
      </div>
    </Tooltip>
  )
}

export type NpColorSelectProps = {
  options: NpColorOption[]
  activeIndex: number
  onActiveIndexChange: (value: number) => void
}

export const NpColorSelect = ({ options, activeIndex, onActiveIndexChange }: NpColorSelectProps) => {
  const ref = useArrowKeys<HTMLButtonElement>((change) =>
    onActiveIndexChange((activeIndex + change + options.length) % options.length),
  )

  return (
    <button ref={ref} type="button" className="inline-flex items-center justify-center gap-2 rounded-full">
      {options.map((option, index) => {
        const active = index === activeIndex

        return (
          <ColorButton
            key={index}
            option={option}
            index={index}
            active={active}
            onActiveIndexChange={onActiveIndexChange}
          />
        )
      })}
    </button>
  )
}
